import React, { useState, useEffect } from "react";
import categoryService from "../../services/categoryService";
import { useNotification } from "../../context/NotificationContext";
import AppTable from "../../components/ui/AppTable";
import ListToolbar from "../../components/ui/ListToolbar";

const CategoryList = ({ onEdit, onView, refreshTrigger }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [status, setStatus] = useState(""); // Empty string means all

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const { showSuccess } = useNotification();

  // Load categories when dependencies change
  useEffect(() => {
    loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, searchTerm, status, refreshTrigger]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const response = await categoryService.getCategories(
        currentPage,
        pageSize,
        searchTerm,
        status
      );

      console.log("API Response:", response); // Debug log

      if (response.success) {
        console.log("Response data:", response.data); // Debug log
        setCategories(response.data.content || []);
        setTotalPages(response.data.totalPages || 0);
        setTotalElements(response.data.totalElements || 0);
      }
    } catch (error) {
      console.error("Error loading categories:", error);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(0);
  };

  const handleStatusChange = (e) => {
    const value = e.target.value;
    // Convert string value to boolean or null
    const statusValue = value === "" ? "" : value === "true";
    setStatus(statusValue);
    setCurrentPage(0);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(parseInt(e.target.value));
    setCurrentPage(0);
  };

  const handleDelete = async (categoryId) => {
    if (window.confirm("Are you sure you want to delete this category?")) {
      try {
        const response = await categoryService.deleteCategory(categoryId);
        if (response.success) {
          showSuccess("Category deleted successfully!");
          loadCategories();
        }
      } catch (error) {
        console.error("Error deleting category:", error);
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const startPage = Math.max(0, currentPage - 2);
    const endPage = Math.min(totalPages - 1, currentPage + 2);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`btn ${
            i === currentPage ? "btn-primary" : "btn-outline-primary"
          } btn-sm mx-1`}
          onClick={() => handlePageChange(i)}
        >
          {i + 1}
        </button>
      );
    }

    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="text-muted">
          Showing {currentPage * pageSize + 1} to{" "}
          {Math.min((currentPage + 1) * pageSize, totalElements)} of{" "}
          {totalElements} entries
        </div>
        <div className="d-flex align-items-center">
          <button
            className="btn btn-outline-primary btn-sm me-2"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 0}
          >
            Previous
          </button>
          {pages}
          <button
            className="btn btn-outline-primary btn-sm ms-2"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages - 1}
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="category-list">
      <ListToolbar
        title="Categories"
        subtitle={`${totalElements} categor${totalElements !== 1 ? "ies" : "y"} found`}
      >
        <div className="row g-3">
          <div className="col-md-4">
            <div className="input-group">
              <span className="input-group-text">
                <i className="fas fa-search"></i>
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search categories..."
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>
          </div>
          <div className="col-md-3">
            <select
              className="form-select"
              value={status === "" ? "" : status.toString()}
              onChange={handleStatusChange}
            >
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
          <div className="col-md-5 d-flex align-items-center justify-content-end">
            <label className="form-label me-2 mb-0">Show:</label>
            <select
              className="form-select"
              value={pageSize}
              onChange={handlePageSizeChange}
              style={{ width: "auto" }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      </ListToolbar>

      {/* Categories Table */}
      <div className="card border-0 shadow-sm">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted mt-2">Loading categories...</p>
            </div>
          ) : categories.length === 0 ? (
            <div className="text-center py-5">
              <i
                className="fas fa-tags text-muted mb-3"
                style={{ fontSize: "3rem" }}
              ></i>
              <h5 className="text-muted">No Categories Found</h5>
              <p className="text-muted">
                {searchTerm
                  ? "Try adjusting your search criteria."
                  : "No categories have been created yet."}
              </p>
            </div>
          ) : (
            <AppTable responsive>
                <thead>
                  <tr>
                    <th className="ps-4">Category</th>
                    <th>Parent Category</th>
                    <th className="text-center">Events</th>
                    <th className="text-center">Clubs</th>
                    <th className="text-center">Status</th>
                    <th>Created</th>
                    <th className="text-center pe-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((category) => (
                    <tr key={category.id} className="border-bottom">
                      <td className="ps-4">
                        <div className="d-flex align-items-center">
                          <div
                            className="category-icon rounded-circle bg-success bg-opacity-10 d-flex align-items-center justify-content-center me-3"
                            style={{ width: "45px", height: "45px" }}
                          >
                            <i
                              className={`fas ${
                                category.parentCategoryId ? "fa-tag" : "fa-tags"
                              } text-success`}
                            ></i>
                          </div>
                          <div>
                            <div className="fw-bold text-dark">
                              {category.name}
                            </div>
                            <small className="text-muted">
                              ID: {category.id}
                            </small>
                          </div>
                        </div>
                      </td>
                      <td>
                        {category.parentCategoryName ? (
                          <span className="badge bg-info bg-opacity-10 text-info">
                            <i className="fas fa-sitemap me-1"></i>
                            {category.parentCategoryName}
                          </span>
                        ) : (
                          <span className="badge bg-primary bg-opacity-10 text-primary">
                            <i className="fas fa-crown me-1"></i>
                            Main Category
                          </span>
                        )}
                      </td>
                      <td className="text-center">
                        <span className="badge bg-primary">
                          {category.totalEvents || 0}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className="badge bg-info">
                          {category.totalClubs || 0}
                        </span>
                      </td>
                      <td className="text-center">
                        <span
                          className={`badge ${
                            category.status === true
                              ? "bg-success"
                              : "bg-warning"
                          }`}
                        >
                          {category.status === true && (
                            <i className="fas fa-check-circle me-1"></i>
                          )}
                          {category.status === false && (
                            <i className="fas fa-pause-circle me-1"></i>
                          )}
                          {category.status === true ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>
                        <span className="text-muted">
                          {formatDate(category.createdAt)}
                        </span>
                      </td>
                      <td className="text-center pe-4">
                        <div className="btn-group" role="group">
                          <button
                            className="btn btn-outline-info btn-sm"
                            onClick={() => onView(category)}
                            title="View Details"
                          >
                            <i className="fas fa-eye"></i>
                          </button>
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => onEdit(category)}
                            title="Edit Category"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => handleDelete(category.id)}
                            title="Delete Category"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
            </AppTable>
          )}
        </div>
      </div>

      {/* Pagination */}
      {renderPagination()}
    </div>
  );
};

export default CategoryList;
